/**
 * Stripe zero-decimal API currencies — no fractional units. ISK and UGX are
 * intentionally excluded: Stripe requires two-decimal API representation
 * ending in 00 for those special-case currencies.
 * https://docs.stripe.com/currencies#zero-decimal
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
  'PYG', 'RWF', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

const SPECIAL_TWO_DECIMAL_API_CURRENCIES = new Set(['ISK', 'UGX']);

export function minorUnitQuantum(currency) {
  if (typeof currency !== 'string') return null;
  const code = currency.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) return null;
  return SPECIAL_TWO_DECIMAL_API_CURRENCIES.has(code) ? 100 : 1;
}

/** Round tax once, half-up, to an amount Stripe can represent. */
export function calculateTaxMinor(taxableMinor, rateBasisPoints, currency) {
  const quantum = minorUnitQuantum(currency);
  if (
    quantum == null ||
    !Number.isSafeInteger(taxableMinor) ||
    taxableMinor < 0 ||
    !Number.isSafeInteger(rateBasisPoints) ||
    rateBasisPoints < 0 ||
    rateBasisPoints > 10000
  ) {
    return null;
  }
  const product = taxableMinor * rateBasisPoints;
  if (!Number.isSafeInteger(product)) return null;
  const denominator = 10000 * quantum;
  const rounded = Math.floor((product + denominator / 2) / denominator) * quantum;
  return Number.isSafeInteger(rounded) ? rounded : null;
}

/**
 * Formats a smallest-unit price with currency symbol.
 *
 * Prices are stored in the smallest currency unit (Stripe convention):
 *   1999 + "USD" → "$19.99"
 *   500  + "JPY" → "¥500"
 *
 * @param {number} smallestUnitPrice - Price in smallest currency unit (e.g., cents)
 * @param {string} currency - ISO 4217 currency code (e.g., 'USD', 'JPY')
 * @returns {string} Formatted price string, or '' if invalid
 */
export function formatPrice(smallestUnitPrice, currency) {
  if (
    !Number.isSafeInteger(smallestUnitPrice) ||
    smallestUnitPrice < 0 ||
    !currency ||
    typeof currency !== 'string'
  ) {
    return '';
  }

  const currencyCode = currency.trim().toUpperCase();
  if (currencyCode.length !== 3 || !/^[A-Z]{3}$/.test(currencyCode)) {
    return '';
  }

  if (
    SPECIAL_TWO_DECIMAL_API_CURRENCIES.has(currencyCode) &&
    smallestUnitPrice % 100 !== 0
  ) {
    return '';
  }

  const divisor = ZERO_DECIMAL_CURRENCIES.has(currencyCode) ? 1 : 100;
  const displayAmount = smallestUnitPrice / divisor;

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(displayAmount);
  } catch (error) {
    console.warn(`Invalid currency code: ${currency}`, error);
    return '';
  }
}

/**
 * Resolve a catalog amount for customer-facing display during the controlled
 * price schema migration.
 *
 * `unitAmount` is the Stripe-aligned canonical field. The legacy `price`
 * fallback is deliberately confined to display/sorting code so an unstamped
 * product can never become payment authority through this helper.
 */
export function getDisplayUnitAmount(priceInfo) {
  if (!priceInfo || typeof priceInfo !== 'object') {
    return null;
  }

  const hasCanonical = Object.prototype.hasOwnProperty.call(
    priceInfo,
    'unitAmount',
  );
  const hasLegacy = Object.prototype.hasOwnProperty.call(priceInfo, 'price');
  const canonical = priceInfo.unitAmount;
  const legacy = priceInfo.price;

  if (hasCanonical) {
    if (!Number.isSafeInteger(canonical) || canonical < 0) {
      return null;
    }
    if (
      hasLegacy &&
      (!Number.isSafeInteger(legacy) || legacy < 0 || legacy !== canonical)
    ) {
      return null;
    }
    return canonical;
  }

  if (hasLegacy && Number.isSafeInteger(legacy) && legacy >= 0) {
    return legacy;
  }

  return null;
}

/**
 * Checks if a product has valid price information to display
 *
 * @param {object} product - The product object from Firestore
 * @returns {boolean} True if the product has valid price info
 */
export function hasValidPrice(product) {
  const unitAmount = getDisplayUnitAmount(product?.priceInfo);
  return (
    unitAmount != null &&
    product.priceInfo.currency
  );
}

/**
 * Gets the formatted price for a product if available
 *
 * @param {object} product - The product object from Firestore
 * @returns {string|null} Formatted price string or null if no valid price
 */
export function getFormattedProductPrice(product) {
  if (!hasValidPrice(product)) {
    return null;
  }

  return formatPrice(
    getDisplayUnitAmount(product.priceInfo),
    product.priceInfo.currency,
  );
}
